import { useState } from "react";
import { Stage, uid } from "./businessSetup";

type StageEditorProps = {
  stages: Stage[];
  usedCounts: Record<string, number>;
  embedded?: boolean;
  onClose: () => void;
  onSave: (stages: Stage[]) => void;
};

const COLORS = ["#f97316", "#3b82f6", "#7c3aed", "#16a34a", "#ef4444", "#64748b", "#0ea5e9", "#eab308"];

function sameName(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function blankStage(index: number): Stage {
  return {
    id: uid(),
    name: "",
    color: COLORS[index % COLORS.length],
    description: "",
    aiOn: true,
    requireAttribute: false,
    attributeName: "",
  };
}

export default function StageEditor({ stages, usedCounts, embedded, onClose, onSave }: StageEditorProps) {
  const [rows, setRows] = useState<Stage[]>(() => stages.map((stage) => ({ ...stage })));
  const [draft, setDraft] = useState<Stage>(() => blankStage(stages.length));
  const [error, setError] = useState("");

  const update = (index: number, patch: Partial<Stage>) => {
    const copy = rows.slice();
    copy[index] = { ...copy[index], ...patch };
    setRows(copy);
  };

  const move = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= rows.length) return;
    const copy = rows.slice();
    const [item] = copy.splice(index, 1);
    copy.splice(next, 0, item);
    setRows(copy);
  };

  const addDraft = () => {
    const name = draft.name.trim();
    if (!name) {
      setError("Type a name for the new stage.");
      return;
    }
    if (rows.some((stage) => sameName(stage.name, name))) {
      setError("That stage name is already used. Each stage needs its own name.");
      return;
    }
    if (draft.requireAttribute && !draft.attributeName.trim()) {
      setError("Name the field a lead must have before it can enter this stage.");
      return;
    }
    setRows(rows.concat({ ...draft, name, description: draft.description.trim(), attributeName: draft.attributeName.trim() }));
    setDraft(blankStage(rows.length + 1));
    setError("");
  };

  const save = () => {
    let next = rows.slice();
    const pending = draft.name.trim();
    if (pending) {
      if (next.some((stage) => sameName(stage.name, pending))) {
        setError("That stage name is already used. Each stage needs its own name.");
        return;
      }
      if (draft.requireAttribute && !draft.attributeName.trim()) {
        setError("Name the field a lead must have before it can enter this stage.");
        return;
      }
      next = next.concat({
        ...draft,
        name: pending,
        description: draft.description.trim(),
        attributeName: draft.attributeName.trim(),
      });
    }
    const named = next.map((stage) => ({ ...stage, name: stage.name.trim(), description: stage.description.trim(), attributeName: stage.attributeName.trim() }));
    if (named.some((stage) => !stage.name)) {
      setError("Every stage needs a name.");
      return;
    }
    const seen = new Set<string>();
    for (const stage of named) {
      const key = stage.name.toLowerCase();
      if (seen.has(key)) {
        setError(`“${stage.name}” is used more than once. Each stage needs its own name.`);
        return;
      }
      seen.add(key);
      if (stage.requireAttribute && !stage.attributeName) {
        setError(`“${stage.name}” asks for a field, but the field has no name.`);
        return;
      }
    }
    setRows(named);
    setDraft(blankStage(named.length));
    setError("");
    onSave(named);
  };

  return (
    <div className={embedded ? "" : "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"}>
      <div className={embedded ? "flex flex-col" : "flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl"}>
        <div className={`flex items-center justify-between ${embedded ? "mb-4" : "border-b px-5 py-4"}`}>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Stages</h2>
            <p className="text-sm text-gray-500">Create the stages for this business. Each name can be used only once. A new lead starts in the stage named Conversations. If there is no stage with that name, the new lead starts in the first stage.</p>
          </div>
          {embedded ? null : (
            <button type="button" onClick={onClose} className="px-2 text-xl text-gray-400 hover:text-gray-700" aria-label="Close">
              ×
            </button>
          )}
        </div>
        <div className={embedded ? "space-y-3" : "space-y-3 overflow-y-auto px-5 py-4"}>
          {rows.length === 0 ? <p className="text-sm text-gray-500">No stages yet. Add the first one below.</p> : null}
          {rows.map((stage, index) => {
            const used = usedCounts[stage.id] || 0;
            const duplicate = rows.some((other, otherIndex) => otherIndex !== index && sameName(other.name, stage.name) && stage.name.trim());
            return (
              <div key={stage.id} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-start gap-2">
                  <input
                    type="color"
                    value={stage.color}
                    aria-label={`${stage.name || "Stage"} color`}
                    onChange={(event) => update(index, { color: event.target.value })}
                    className="mt-1 h-9 w-9 cursor-pointer rounded border border-gray-200 bg-white p-1"
                  />
                  <div className="min-w-0 flex-1">
                    <input
                      value={stage.name}
                      aria-label="Stage name"
                      onChange={(event) => update(index, { name: event.target.value })}
                      className={`w-full rounded-lg border px-3 py-2 text-sm font-medium ${duplicate ? "border-red-400" : "border-gray-200"}`}
                    />
                    <input
                      value={stage.description}
                      aria-label="Stage description"
                      placeholder="What this stage means"
                      onChange={(event) => update(index, { description: event.target.value })}
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600"
                    />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={stage.aiOn} onChange={(event) => update(index, { aiOn: event.target.checked })} />
                    AI can message
                  </label>
                  <button type="button" onClick={() => move(index, -1)} className="text-gray-500" aria-label="Move stage up">Up</button>
                  <button type="button" onClick={() => move(index, 1)} className="text-gray-500" aria-label="Move stage down">Down</button>
                  <button
                    type="button"
                    onClick={() => setRows(rows.filter((_, rowIndex) => rowIndex !== index))}
                    disabled={used > 0}
                    title={used > 0 ? `${used} leads are still in this stage. Move them first.` : "Remove stage"}
                    className="ml-auto text-red-600 disabled:cursor-not-allowed disabled:text-gray-300"
                  >
                    Remove
                  </button>
                </div>
                <label className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  <span>
                    <span className="block font-medium">Require a field</span>
                    <span className="text-xs text-gray-500">The lead must have this filled in before it can enter this stage.</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={stage.requireAttribute}
                    onChange={(event) => update(index, { requireAttribute: event.target.checked })}
                  />
                </label>
                {stage.requireAttribute ? (
                  <input
                    value={stage.attributeName}
                    placeholder="Field name"
                    aria-label="Required field name"
                    onChange={(event) => update(index, { attributeName: event.target.value })}
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                ) : null}
              </div>
            );
          })}

          <div className="rounded-lg border border-dashed border-gray-300 p-3">
            <p className="text-sm font-medium text-gray-800">Add a stage</p>
            <input
              value={draft.name}
              placeholder="Stage name"
              aria-label="New stage name"
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <input
              value={draft.description}
              placeholder="What this stage means"
              aria-label="New stage description"
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <label className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <span>
                <span className="block font-medium">Require a field</span>
                <span className="text-xs text-gray-500">The lead must have this filled in before it can enter this stage.</span>
              </span>
              <input
                type="checkbox"
                checked={draft.requireAttribute}
                onChange={(event) => setDraft({ ...draft, requireAttribute: event.target.checked })}
              />
            </label>
            {draft.requireAttribute ? (
              <input
                value={draft.attributeName}
                placeholder="Field name"
                aria-label="Required field for the new stage"
                onChange={(event) => setDraft({ ...draft, attributeName: event.target.value })}
                className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            ) : null}
            <button type="button" onClick={addDraft} className="mt-3 text-sm font-medium text-blue-600">
              Add this stage
            </button>
          </div>
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>
        <div className={`flex items-center justify-end gap-3 ${embedded ? "mt-4" : "border-t px-5 py-4"}`}>
          {error ? <p className="mr-auto text-sm text-red-600">{error}</p> : null}
          {embedded ? null : (
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-600">
              Cancel
            </button>
          )}
          <button type="button" onClick={save} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">
            Save stages
          </button>
        </div>
      </div>
    </div>
  );
}
